/*
 * Gift-with-purchase claim module.
 *
 * Reveals itself only when the GWP discount code is on the cart. Klaviyo hands that
 * code out at signup, so the code IS the eligibility signal — there is no separate
 * cohort read and no test-group logic here.
 *
 * The code is read from the cart itself (/cart.js reports applied discount codes and
 * whether each currently applies), not from a flag we set ourselves, so it cannot be
 * forged by editing storage.
 *
 * Fails closed: an unreadable cart, an unexpected shape, or no matching code all mean
 * the module stays hidden. A missing gift is recoverable; a wrongly-given one is not.
 *
 * State lives outside this element — the cart sections are re-rendered on every cart
 * change, which destroys and recreates this DOM. The claimed variant is a cart
 * attribute; a rejection is recorded in sessionStorage.
 *
 * Rejection is deliberately NOT blocking: a customer who removes the gift sees the
 * picker again and can claim it back. The flag only records that it happened.
 *
 * Claims go through /cart/update.js rather than /cart/add.js. That is deliberate: an
 * app on this store wraps window.fetch and resolves successful /cart/add.js responses
 * to undefined. /cart/update.js is unaffected. See the project log.
 */
if (!customElements.get('gift-claim')) {
  const REJECTED_STORAGE_KEY = 'gwp-gift-rejected';

  class GiftClaim extends HTMLElement {
    connectedCallback() {
      this.swatches = this.querySelectorAll('.gift-claim__swatch');
      this.errorEl = this.querySelector('.gift-claim__error');

      this.onSwatchClick = this.onSwatchClick.bind(this);
      this.swatches.forEach((swatch) => swatch.addEventListener('click', this.onSwatchClick));

      // pubsub.js loads deferred in <head>, so this is normally defined — but the
      // module must not break the cart if it ever isn't.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.checkForRemoval());
      }

      this.revealIfEligible();
    }

    disconnectedCallback() {
      this.swatches?.forEach((swatch) => swatch.removeEventListener('click', this.onSwatchClick));
      this.cartUpdateUnsubscriber?.();
    }

    /* Eligibility ------------------------------------------------------------- */

    /**
     * Reveals the module if the GWP discount code is on the cart.
     *
     * Presence only — deliberately NOT `applicable`. For a Buy X Get Y, applicability
     * depends on there being a Y in the cart to discount, and putting the Y there is
     * this component's entire job. Gating on it is circular: no module, so no gift, so
     * never applicable.
     *
     * `applicable` is also ambiguous. Shopify reports false both for "nothing to
     * discount yet" and for "this customer has already used it", and the cart payload
     * does not distinguish them, so it cannot safely gate anything.
     *
     * Verified against the cart on every render rather than cached, so a code removed
     * mid-session stops qualifying immediately.
     */
    async revealIfEligible() {
      if (!this.discountCode) return;
      if (!(await this.hasDiscountApplied())) return;
      this.hidden = false;
    }

    /**
     * Whether the cart currently carries the GWP discount code.
     *
     * cache: 'no-store' is required, not defensive. A cached /cart.js can predate the
     * discount being applied or the cart being filled, and a stale empty response reads
     * as "not eligible" — the module then only appears after a hard refresh.
     */
    async hasDiscountApplied() {
      try {
        const response = await fetch(`${routes.cart_url}.js`, { cache: 'no-store' });
        if (!response || !response.ok) return false;

        const cart = await response.json();
        return this.cartHasCode(cart);
      } catch (error) {
        return false;
      }
    }

    /**
     * Shopify reports cart discount codes as objects with `code` and `applicable`.
     * Bare strings are accepted too, since the shape has varied. `applicable` is
     * intentionally ignored — see revealIfEligible.
     */
    cartHasCode(cart) {
      const codes = cart?.discount_codes;
      if (!Array.isArray(codes)) return false;

      const wanted = this.discountCode.toUpperCase();
      return codes.some((entry) => {
        const code = typeof entry === 'string' ? entry : entry?.code;
        return typeof code === 'string' && code.toUpperCase() === wanted;
      });
    }

    get discountCode() {
      return this.dataset.discountCode || '';
    }

    /* Claiming ---------------------------------------------------------------- */

    onSwatchClick(event) {
      const swatch = event.currentTarget;
      if (swatch.dataset.available !== 'true' || this.isClaiming) return;

      const variantId = swatch.dataset.variantId;
      if (!variantId) return;

      this.claim(variantId, swatch);
    }

    async claim(variantId, swatch) {
      this.isClaiming = true;
      this.setError(false);
      swatch.classList.add('is-loading');

      try {
        const response = await fetch(routes.cart_update_url, {
          ...fetchConfig(),
          body: JSON.stringify({
            updates: { [variantId]: 1 },
            attributes: { _gwp_claim: String(variantId) },
            sections: this.sectionsToRender(),
            sections_url: window.location.pathname,
          }),
        });

        // A wrapped fetch can resolve to undefined — treat it as a failure rather
        // than dereferencing it, so the customer sees a retry instead of a dead UI.
        if (!response || !response.ok) throw new Error('Cart update failed');

        const data = await response.json();
        this.renderSections(data);
      } catch (error) {
        swatch.classList.remove('is-loading');
        this.isClaiming = false;
        this.setError(true);
      }
    }

    sectionsToRender() {
      const sections = ['cart-icon-bubble'];
      if (document.getElementById('CartDrawer')) sections.push('cart-drawer');

      const cartItems = document.getElementById('main-cart-items');
      if (cartItems?.dataset.id) sections.push(cartItems.dataset.id);

      const cartFooter = document.getElementById('main-cart-footer');
      if (cartFooter?.dataset.id) sections.push(cartFooter.dataset.id);

      return Array.from(new Set(sections));
    }

    /**
     * Swaps re-rendered cart HTML in. This element is inside those containers, so it
     * is destroyed here — connectedCallback runs again on the replacement.
     */
    renderSections(data) {
      if (!data?.sections) return;

      const drawer = document.getElementById('CartDrawer');
      if (drawer && data.sections['cart-drawer']) {
        const parsed = new DOMParser().parseFromString(data.sections['cart-drawer'], 'text/html');
        const replacement = parsed.getElementById('CartDrawer');
        if (replacement) drawer.innerHTML = replacement.innerHTML;
      }

      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && data.sections['cart-icon-bubble']) {
        const parsed = new DOMParser().parseFromString(data.sections['cart-icon-bubble'], 'text/html');
        const replacement = parsed.querySelector('#cart-icon-bubble .shopify-section, #cart-icon-bubble');
        if (replacement) bubble.innerHTML = replacement.innerHTML;
      }

      [
        { id: 'main-cart-items', selector: '.js-contents' },
        { id: 'main-cart-footer', selector: '.js-contents-totals' },
      ].forEach(({ id, selector }) => {
        const scope = document.getElementById(id);
        const sectionId = scope?.dataset.id;
        if (!scope || !sectionId || !data.sections[sectionId]) return;

        const parsed = new DOMParser().parseFromString(data.sections[sectionId], 'text/html');
        const replacement = parsed.querySelector(selector);
        const container = scope.querySelector(selector);
        if (replacement && container) container.innerHTML = replacement.innerHTML;
      });
    }

    /* Removal ----------------------------------------------------------------- */

    /**
     * Records that a claimed gift was removed. The picker renders again either way,
     * so the customer can claim it back — this only leaves a trace that they said no.
     */
    async checkForRemoval() {
      const claimedId = this.dataset.claimedVariantId;
      if (!claimedId) return;

      try {
        const response = await fetch(`${routes.cart_url}.js`, { cache: 'no-store' });
        if (!response || !response.ok) return;

        const cart = await response.json();
        const stillPresent = cart.items?.some((item) => String(item.variant_id) === claimedId);
        if (stillPresent) return;

        this.rememberRejection(claimedId);

        // Clear the pointer so a later re-add of the same variant is not mistaken
        // for the original claim.
        await fetch(routes.cart_update_url, {
          ...fetchConfig(),
          body: JSON.stringify({ attributes: { _gwp_claim: '' } }),
        });
      } catch (error) {
        // Never let this bookkeeping break the cart.
      }
    }

    rememberRejection(variantId) {
      try {
        window.sessionStorage.setItem(REJECTED_STORAGE_KEY, String(variantId));
      } catch (error) {
        // Private browsing and blocked storage are fine — this is a record, not a gate.
      }
    }

    /** Whether this visitor has already turned the gift down this session. */
    static hasRejected() {
      try {
        return window.sessionStorage.getItem(REJECTED_STORAGE_KEY) !== null;
      } catch (error) {
        return false;
      }
    }

    /* UI ---------------------------------------------------------------------- */

    setError(show) {
      if (this.errorEl) this.errorEl.hidden = !show;
    }
  }

  customElements.define('gift-claim', GiftClaim);
}
